import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
	useGetOcrVerify,
	usePostOcrExtractFoliar,
	usePostOcrExtractSoil,
	type OcrFoliarSample,
	type OcrSoilSample,
	type OcrWarning,
} from '../../api';
import { useUserOcrStorage } from '../hooks/useUserOcrStorage';
import { OCR_TIMEOUT_MS, OCR_TIMEOUT_MESSAGE } from '../../api/hooks/ocr/ocr.constants';

export type OcrQueueAnalysisType = 'soil' | 'foliar';
export type OcrQueueStatus = 'uploading' | 'queued' | 'processing' | 'done' | 'error';

export interface OcrQueueItem {
	id: string;
	fileName: string;
	fileSize?: number;
	analysisType: OcrQueueAnalysisType;
	status: OcrQueueStatus;
	statusChangedAt?: number;
	jobId?: string;
	samples?: Array<OcrSoilSample | OcrFoliarSample>;
	sampleIndex: number;
	warnings?: OcrWarning[];
	error?: string;
}

interface OcrQueueContextValue {
	items: OcrQueueItem[];
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	addFiles: (files: File[], analysisType: OcrQueueAnalysisType) => void;
	getItem: (id: string) => OcrQueueItem | undefined;
	advanceSample: (id: string) => boolean;
	remove: (id: string) => void;
	removeMany: (ids: string[]) => void;
}

const STORAGE_KEY = 'practium:ocr-queue';
const OcrQueueContext = createContext<OcrQueueContextValue | null>(null);
const createId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const decodeItems = (value: unknown): OcrQueueItem[] => Array.isArray(value)
	? value.map((item) => ({ ...item, sampleIndex: item.sampleIndex ?? 0, statusChangedAt: item.statusChangedAt ?? Date.now() }))
	: [];

export const OcrQueueProvider = ({ children }: { children: ReactNode }) => {
	const [items, setItems, isCurrent] = useUserOcrStorage<OcrQueueItem[]>(STORAGE_KEY, [], decodeItems);
	const [isOpen, setIsOpen] = useState(true);
	const soilMutation = usePostOcrExtractSoil();
	const foliarMutation = usePostOcrExtractFoliar();

	useEffect(() => {
		const activeItems = items.filter((item) =>
			['uploading', 'queued', 'processing'].includes(item.status)
		);
		if (!activeItems.length) return;
		const deadline = Math.min(...activeItems.map((item) => (item.statusChangedAt ?? Date.now()) + OCR_TIMEOUT_MS));
		const timer = window.setTimeout(() => {
			const now = Date.now();
			setItems((current) => current.map((item) =>
				['uploading', 'queued', 'processing'].includes(item.status) &&
				now - (item.statusChangedAt ?? now) >= OCR_TIMEOUT_MS
					? { ...item, status: 'error', error: OCR_TIMEOUT_MESSAGE }
					: item
			));
		}, Math.max(0, deadline - Date.now()));
		return () => window.clearTimeout(timer);
	}, [items]);

	const pendingItems = items.filter(
		(item) => item.jobId && (item.status === 'queued' || item.status === 'processing')
	);
	const verificationQueries = useGetOcrVerify<OcrSoilSample | OcrFoliarSample>(pendingItems.map((item) => item.jobId));

	useEffect(() => {
		verificationQueries.forEach((query, index) => {
			const item = pendingItems[index];
			if (!item || !query.data) return;
			const response = query.data;
			const nextStatus: OcrQueueStatus = response.status === 'failed' ? 'error' : response.status;
			if (item.status === nextStatus && (nextStatus !== 'done' || item.samples)) return;
			setItems((current) =>
				current.map((entry) =>
					entry.id === item.id && (entry.status === 'queued' || entry.status === 'processing')
						? {
							...entry,
							status: nextStatus,
							statusChangedAt: Date.now(),
							samples: response.result?.samples,
							warnings: response.warnings ?? [],
							error: response.error?.message,
						}
						: entry
				)
			);
		});
	}, [verificationQueries, pendingItems]);

	const addFiles = (files: File[], analysisType: OcrQueueAnalysisType) => {
		if (!isCurrent()) return;
		const newItems = files.map((file) => ({
			id: createId(),
			fileName: file.name,
			fileSize: file.size,
			analysisType,
			status: 'uploading' as const,
			statusChangedAt: Date.now(),
			sampleIndex: 0,
		}));
		setItems((current) => [...current, ...newItems]);

		newItems.forEach(async (item, index) => {
			try {
				const mutation = analysisType === 'soil' ? soilMutation : foliarMutation;
				const response = await mutation.mutateAsync({ file: files[index] });
				setItems((current) =>
					current.map((entry) =>
						entry.id === item.id && entry.status === 'uploading'
							? { ...entry, status: 'queued', jobId: response.jobId, statusChangedAt: Date.now() }
							: entry
					)
				);
			} catch (error) {
				setItems((current) =>
					current.map((entry) =>
						entry.id === item.id && entry.status === 'uploading'
							? {
									...entry,
									status: 'error',
									error: error instanceof Error ? error.message : 'Falha no envio',
								  }
							: entry
					)
				);
			}
		});
	};

	const value = useMemo(
		() => ({
			items,
			isOpen,
			setIsOpen,
			addFiles,
			getItem: (id: string) => items.find((item) => item.id === id),
			advanceSample: (id: string) => {
				const item = items.find((entry) => entry.id === id);
				if (!item?.samples || item.sampleIndex >= item.samples.length - 1) return false;
				setItems((current) =>
					current.map((entry) =>
						entry.id === id ? { ...entry, sampleIndex: entry.sampleIndex + 1 } : entry
					)
				);
				return true;
			},
			remove: (id: string) => setItems((current) => current.filter((item) => item.id !== id)),
			removeMany: (ids: string[]) => {
				const removedIds = new Set(ids);
				setItems((current) => current.filter((item) => !removedIds.has(item.id)));
			},
			clearFinished: () =>
				setItems((current) => current.filter((item) => item.status !== 'done' && item.status !== 'error')),
		}),
		[items, isOpen, setItems, isCurrent]
	);

	return <OcrQueueContext.Provider value={value}>{children}</OcrQueueContext.Provider>;
};

export const useOcrQueue = () => {
	const context = useContext(OcrQueueContext);
	if (!context) throw new Error('useOcrQueue deve ser usado dentro de OcrQueueProvider');
	return context;
};
